import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Heading,
  Text,
  Button,
  IconButton,
  Switch,
  FormControl,
  FormLabel,
  Input,
  Wrap,
  WrapItem,
  Tag,
  Divider,
  Spinner,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { ArrowBackIcon, AddIcon, EditIcon } from '@chakra-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../services/apiClient';
import { getLocations } from '../../organization/api/organizationApi';
import {
  useScenario,
  useSaveScenarioContent,
  useSetScenarioActive,
  useSetScenarioLocations,
  useUpdateScheduling,
  useDeleteScenario,
} from '../hooks/useScenarios';
import { ScenarioFormModal } from '../components/ScenarioFormModal';
import { TimeBlockEditor, type EditBlock } from '../components/TimeBlockEditor';
import { AddPlaylistModal } from '../components/AddPlaylistModal';
import type { BlockPlaylist } from '../components/SortablePlaylistList';
import type { PlaybackMode } from '../types';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun (.NET DayOfWeek: 0 = Sunday)
const normTime = (t: string) => (t.length === 5 ? `${t}:00` : t);

export function ScenarioDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const scenarioId = id ?? '';

  const { data: detail, isLoading, isError } = useScenario(id);
  const { data: orgLocations } = useQuery({ queryKey: ['organization', 'locations'], queryFn: getLocations });
  const saveContent = useSaveScenarioContent(scenarioId);
  const setActive = useSetScenarioActive(scenarioId);
  const setLocations = useSetScenarioLocations(scenarioId);
  const updateScheduling = useUpdateScheduling(scenarioId);
  const deleteScenario = useDeleteScenario();

  const editModal = useDisclosure();
  const [addingToBlock, setAddingToBlock] = useState<string | null>(null);
  const newKeyRef = useRef(0);

  const [blocks, setBlocks] = useState<EditBlock[]>([]);
  const [blocksDirty, setBlocksDirty] = useState(false);
  const [syncedAt, setSyncedAt] = useState('');
  const [schedStart, setSchedStart] = useState('');
  const [schedEnd, setSchedEnd] = useState('');
  const [schedDays, setSchedDays] = useState<number[]>([]);
  const [locationIds, setLocationIds] = useState<string[]>([]);

  useEffect(() => {
    if (detail && detail.updatedAt !== syncedAt) {
      setBlocks(
        [...detail.timeBlocks]
          .sort((a, b) => a.position - b.position)
          .map((b) => ({
            key: b.id,
            startTime: b.startTime.slice(0, 5),
            endTime: b.endTime.slice(0, 5),
            playbackMode: b.playbackMode,
            playlists: [...b.playlists]
              .sort((a, c) => a.position - c.position)
              .map((p) => ({ playlistId: p.playlistId, playlistName: p.playlistName, trackCount: p.trackCount })),
          })),
      );
      setSchedStart(detail.startDate?.slice(0, 10) ?? '');
      setSchedEnd(detail.endDate?.slice(0, 10) ?? '');
      setSchedDays(detail.daysOfWeek ?? []);
      setLocationIds(detail.locations.map((l) => l.locationId));
      setBlocksDirty(false);
      setSyncedAt(detail.updatedAt);
    }
  }, [detail, syncedAt]);

  if (isLoading) {
    return (
      <Flex justify="center" py={20}>
        <Spinner size="xl" color="brand.500" thickness="3px" />
      </Flex>
    );
  }
  if (isError || !detail) {
    return (
      <VStack py={20} spacing={3}>
        <Text fontWeight="600" color="gray.600">
          {t('scenarios.notFound')}
        </Text>
        <Button variant="outline" onClick={() => navigate('/scenarios')}>
          {t('scenarios.backToList')}
        </Button>
      </VStack>
    );
  }

  const patchBlock = (key: string, fn: (b: EditBlock) => EditBlock) => {
    setBlocks((prev) => prev.map((b) => (b.key === key ? fn(b) : b)));
    setBlocksDirty(true);
  };

  const addBlock = () => {
    newKeyRef.current += 1;
    setBlocks((prev) => [
      ...prev,
      { key: `new-${newKeyRef.current}`, startTime: '12:00', endTime: '13:00', playbackMode: null, playlists: [] },
    ]);
    setBlocksDirty(true);
  };

  const addPlaylistsToBlock = (key: string, added: BlockPlaylist[]) => {
    patchBlock(key, (b) => {
      const have = new Set(b.playlists.map((p) => p.playlistId));
      return { ...b, playlists: [...b.playlists, ...added.filter((p) => !have.has(p.playlistId))] };
    });
  };

  const saveBlocks = async () => {
    try {
      await saveContent.mutateAsync({
        timeBlocks: blocks.map((b) => ({
          startTime: normTime(b.startTime),
          endTime: normTime(b.endTime),
          playbackMode: b.playbackMode,
          playlistIds: b.playlists.map((p) => p.playlistId),
        })),
      });
      toast({ status: 'success', title: t('scenarios.contentSaved'), duration: 2000 });
    } catch (e) {
      toast({ status: 'error', title: e instanceof Error ? e.message : t('common.error', 'Something went wrong') });
    }
  };

  const handleActiveToggle = () => {
    setActive.mutate(!detail.isActive, {
      onError: (e) =>
        toast({
          status: 'error',
          title:
            e instanceof ApiError && e.status === 400
              ? t('scenarios.activateNeedsPlaylist')
              : t('common.error', 'Something went wrong'),
        }),
    });
  };

  const toggleLocation = (locId: string) => {
    const prev = locationIds;
    const next = prev.includes(locId) ? prev.filter((x) => x !== locId) : [...prev, locId];
    setLocationIds(next);
    setLocations.mutate(next, {
      onError: () => {
        setLocationIds(prev);
        toast({ status: 'error', title: t('common.error', 'Something went wrong') });
      },
    });
  };

  const toggleDay = (d: number) =>
    setSchedDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const saveSchedule = () => {
    updateScheduling.mutate(
      {
        startDate: schedStart || null,
        endDate: schedEnd || null,
        daysOfWeek: schedDays.length ? schedDays : null,
      },
      {
        onSuccess: () => toast({ status: 'success', title: t('scenarios.scheduleSaved'), duration: 2000 }),
        onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
      },
    );
  };

  const handleDelete = () => {
    if (!window.confirm(t('scenarios.deleteConfirmNamed', { name: detail.name }))) return;
    deleteScenario.mutate(detail.id, {
      onSuccess: () => {
        toast({ status: 'success', title: t('scenarios.deleted'), duration: 2000 });
        navigate('/scenarios');
      },
      onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
    });
  };

  const addingBlock = blocks.find((b) => b.key === addingToBlock);

  return (
    <Box maxW="900px">
      <Button variant="link" color="gray.500" leftIcon={<ArrowBackIcon />} mb={4} onClick={() => navigate('/scenarios')}>
        {t('scenarios.backToList')}
      </Button>

      <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap" mb={6}>
        <Box minW={0}>
          <Heading size="lg" color="gray.800" noOfLines={2}>
            {detail.name}
          </Heading>
          {detail.description && (
            <Text color="gray.500" mt={1} noOfLines={2}>
              {detail.description}
            </Text>
          )}
        </Box>
        <HStack>
          <FormControl display="flex" alignItems="center" w="auto">
            <FormLabel htmlFor="active" mb={0} mr={2} fontSize="sm" color="gray.600">
              {t('scenarios.active')}
            </FormLabel>
            <Switch
              id="active"
              isChecked={detail.isActive}
              onChange={handleActiveToggle}
              isDisabled={setActive.isPending}
              colorScheme="green"
            />
          </FormControl>
          <Button leftIcon={<EditIcon />} variant="outline" size="sm" onClick={editModal.onOpen}>
            {t('scenarios.edit')}
          </Button>
          <Menu>
            <MenuButton as={IconButton} aria-label={t('common.more', 'More')} icon={<Text fontSize="lg" lineHeight={1}>⋮</Text>} variant="outline" size="sm" />
            <MenuList>
              <MenuItem color="red.500" onClick={handleDelete}>
                {t('scenarios.delete')}
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {/* Time blocks */}
      <Flex justify="space-between" align="center" mb={3}>
        <Heading size="md" color="gray.700">
          {t('scenarios.timeBlocks')}
        </Heading>
        <Button
          size="sm"
          colorScheme="brand"
          onClick={saveBlocks}
          isLoading={saveContent.isPending}
          isDisabled={!blocksDirty}
        >
          {t('scenarios.saveContent')}
        </Button>
      </Flex>
      <VStack align="stretch" spacing={3} mb={3}>
        {blocks.length === 0 ? (
          <Text color="gray.400" py={4} textAlign="center">
            {t('scenarios.noBlocks')}
          </Text>
        ) : (
          blocks.map((b) => (
            <TimeBlockEditor
              key={b.key}
              block={b}
              onChangeTimes={(start, end) => patchBlock(b.key, (blk) => ({ ...blk, startTime: start, endTime: end }))}
              onChangePlaybackMode={(mode: PlaybackMode | null) => patchBlock(b.key, (blk) => ({ ...blk, playbackMode: mode }))}
              onReorderPlaylists={(orderedIds) =>
                patchBlock(b.key, (blk) => ({
                  ...blk,
                  playlists: orderedIds
                    .map((pid) => blk.playlists.find((p) => p.playlistId === pid))
                    .filter((p): p is BlockPlaylist => !!p),
                }))
              }
              onRemovePlaylist={(pid) => patchBlock(b.key, (blk) => ({ ...blk, playlists: blk.playlists.filter((p) => p.playlistId !== pid) }))}
              onAddPlaylists={() => setAddingToBlock(b.key)}
              onDelete={() => {
                setBlocks((prev) => prev.filter((x) => x.key !== b.key));
                setBlocksDirty(true);
              }}
            />
          ))
        )}
      </VStack>
      <Button variant="outline" size="sm" leftIcon={<AddIcon />} onClick={addBlock}>
        {t('scenarios.addBlock')}
      </Button>

      <Divider my={8} />

      {/* Scheduling */}
      <Heading size="md" color="gray.700" mb={3}>
        {t('scenarios.schedule')}
      </Heading>
      <HStack spacing={4} mb={3} align="flex-end" wrap="wrap">
        <FormControl maxW="200px">
          <FormLabel fontSize="sm">{t('scenarios.startDate')}</FormLabel>
          <Input type="date" value={schedStart} onChange={(e) => setSchedStart(e.target.value)} />
        </FormControl>
        <FormControl maxW="200px">
          <FormLabel fontSize="sm">{t('scenarios.endDate')}</FormLabel>
          <Input type="date" value={schedEnd} onChange={(e) => setSchedEnd(e.target.value)} />
        </FormControl>
      </HStack>
      <Text fontSize="sm" color="gray.500" mb={2}>
        {t('scenarios.daysOfWeek')}
      </Text>
      <Wrap mb={4} spacing={2}>
        {WEEKDAYS.map((d) => (
          <WrapItem key={d}>
            <Tag
              colorScheme={schedDays.includes(d) ? 'brand' : 'gray'}
              borderRadius="full"
              cursor="pointer"
              onClick={() => toggleDay(d)}
            >
              {t(`scenarios.weekdays.${d}`)}
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
      <Button size="sm" colorScheme="brand" variant="outline" onClick={saveSchedule} isLoading={updateScheduling.isPending}>
        {t('scenarios.saveSchedule')}
      </Button>

      <Divider my={8} />

      {/* Locations */}
      <Heading size="md" color="gray.700" mb={3}>
        {t('scenarios.locations')}
      </Heading>
      {orgLocations && orgLocations.length > 0 ? (
        <VStack align="stretch" spacing={2}>
          {orgLocations.map((loc) => (
            <Checkbox
              key={loc.id}
              isChecked={locationIds.includes(loc.id)}
              onChange={() => toggleLocation(loc.id)}
              colorScheme="brand"
            >
              {loc.name}
            </Checkbox>
          ))}
        </VStack>
      ) : (
        <Text color="gray.400" fontSize="sm">
          {t('scenarios.noLocations')}
        </Text>
      )}

      <ScenarioFormModal isOpen={editModal.isOpen} onClose={editModal.onClose} scenario={detail} />
      <AddPlaylistModal
        isOpen={!!addingToBlock}
        onClose={() => setAddingToBlock(null)}
        existingPlaylistIds={new Set(addingBlock?.playlists.map((p) => p.playlistId) ?? [])}
        onAdd={(added) => {
          if (addingToBlock) addPlaylistsToBlock(addingToBlock, added);
        }}
      />
    </Box>
  );
}

export default ScenarioDetailPage;
